import { useState, useEffect, useCallback } from 'react';
import featureService from '../services/featureService';
import type { Feature, MenuStructure } from '../types/features';
import { getDigitalAssistantAddonStatus } from '../services/digitalAssistantAddonService';
import { useAuth } from './useAuth';

export interface UseFeaturesReturn {
  features: Feature[];
  menuStructure: MenuStructure;
  loading: boolean;
  error: string | null;
  hasFeature: (featureName: string) => boolean;
  hasMenuAccess: (menuPath: string) => boolean;
  getFeaturesByCategory: (category: Feature['category']) => Feature[];
  getFeatureLimits: (featureName: string) => Record<string, any> | null;
  hasFeatureLimit: (featureName: string, limitKey: string) => boolean;
  getFeatureLimitValue: (featureName: string, limitKey: string) => any;
  refreshFeatures: () => Promise<void>;
}

export const useFeatures = (): UseFeaturesReturn => {
  const { agency } = useAuth();
  const [features, setFeatures] = useState<Feature[]>([]);
  const [menuStructure, setMenuStructure] = useState<MenuStructure>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const loadFeatures = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      
      // For now, we'll use a simplified approach where all features are available
      // In the future, this can be enhanced to check plan-specific restrictions
      const userMenu = await featureService.getUserMenu();
      
      // Create features list from menu structure
      const allFeatures: Feature[] = [];
      
      // Add root menu items as features
      if (userMenu.root) {
        userMenu.root.forEach((item, index) => {
          allFeatures.push({
            _id: item.path.replace('/', ''),
            name: item.path.replace('/', ''),
            category: 'menu',
            menuItem: {
              label: item.label,
              path: item.path,
              icon: item.icon || 'Home',
              order: item.order || index + 1
            },
            isActive: true,
            sortOrder: item.order || index + 1
          });
        });
      }
      
      // Add submenu items as features
      Object.entries(userMenu).forEach(([parent, items]) => {
        if (parent !== 'root' && Array.isArray(items)) {
          items.forEach((item, index) => {
            allFeatures.push({
              _id: item.path.replace('/', ''),
              name: item.path.replace('/', ''),
              category: 'menu',
              menuItem: {
                label: item.label,
                path: item.path,
                icon: item.icon || 'Home',
                order: item.order || index + 1,
                parent: parent
              },
              isActive: true,
              sortOrder: item.order || index + 1
            });
          });
        }
      });
      
      // Digital Assistant only when agency has an active add-on subscription
      let assistantEnabled = false;
      if (agency?.slug) {
        try {
          const status = await getDigitalAssistantAddonStatus(agency.slug);
          assistantEnabled = !!status.enabled;
        } catch {
          assistantEnabled = false;
        }
      }
      if (assistantEnabled) {
        allFeatures.push({
          _id: 'property_assistant',
          name: 'property_assistant',
          category: 'functionality',
          isActive: true,
          sortOrder: 100,
          description: 'Digital Property Assistant for agency agents',
        });
      }

      setFeatures(allFeatures);
      setMenuStructure(userMenu);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load features');
      console.error('Error loading features:', err);
      
      // Provide fallback data when API fails (no assistant — requires add-on)
      const fallbackFeatures: Feature[] = [
        {
          _id: 'dashboard',
          name: 'dashboard',
          category: 'menu',
          menuItem: {
            label: 'Dashboard',
            path: '/dashboard',
            icon: 'Dashboard',
            order: 1
          },
          isActive: true,
          sortOrder: 1
        },
        {
          _id: 'properties',
          name: 'properties',
          category: 'menu',
          menuItem: {
            label: 'Properties',
            path: '/properties',
            icon: 'Home',
            order: 2
          },
          isActive: true,
          sortOrder: 2
        },
      ];
      
      const fallbackMenu: MenuStructure = {
        root: [
          {
            label: 'Dashboard',
            path: '/dashboard',
            icon: 'Dashboard',
            order: 1
          },
          {
            label: 'Properties',
            path: '/properties',
            icon: 'Home',
            order: 2
          }
        ]
      };
      
      setFeatures(fallbackFeatures);
      setMenuStructure(fallbackMenu);
    } finally {
      setLoading(false);
    }
  }, [agency?.slug]);

  useEffect(() => {
    loadFeatures();
  }, [loadFeatures]);

  const hasFeature = useCallback((featureName: string): boolean => {
    return features.some(feature => feature.name === featureName && feature.isActive);
  }, [features]);

  const hasMenuAccess = useCallback((menuPath: string): boolean => {
    // Check root level menu items
    if (menuStructure.root) {
      const hasRootAccess = menuStructure.root.some(item => item.path === menuPath);
      if (hasRootAccess) return true;
    }

    // Check submenu items
    for (const [parent, items] of Object.entries(menuStructure)) {
      if (parent !== 'root' && Array.isArray(items)) {
        const hasSubmenuAccess = items.some(item => item.path === menuPath);
        if (hasSubmenuAccess) return true;
      }
    }

    return false;
  }, [menuStructure]);

  const getFeaturesByCategory = useCallback((category: Feature['category']): Feature[] => {
    return features.filter(feature => feature.category === category);
  }, [features]);

  const getFeatureLimits = useCallback((featureName: string): Record<string, any> | null => {
    const feature = features.find(f => f.name === featureName);
    return feature?.limits || null;
  }, [features]);

  const hasFeatureLimit = useCallback((featureName: string, limitKey: string): boolean => {
    const limits = getFeatureLimits(featureName);
    return limits ? limitKey in limits : false;
  }, [getFeatureLimits]);

  const getFeatureLimitValue = useCallback((featureName: string, limitKey: string): any => {
    const limits = getFeatureLimits(featureName);
    return limits ? limits[limitKey] : null;
  }, [getFeatureLimits]);

  const refreshFeatures = useCallback(async () => {
    featureService.clearCache();
    await loadFeatures();
  }, [loadFeatures]);

  return {
    features,
    menuStructure,
    loading,
    error,
    hasFeature,
    hasMenuAccess,
    getFeaturesByCategory,
    getFeatureLimits,
    hasFeatureLimit,
    getFeatureLimitValue,
    refreshFeatures
  };
};

// Hook for checking a single feature
export const useFeature = (featureName: string) => {
  const { hasFeature, getFeatureLimits, hasFeatureLimit, getFeatureLimitValue, loading } = useFeatures();
  
  return {
    hasAccess: hasFeature(featureName),
    limits: getFeatureLimits(featureName),
    hasLimit: (limitKey: string) => hasFeatureLimit(featureName, limitKey),
    getLimitValue: (limitKey: string) => getFeatureLimitValue(featureName, limitKey),
    loading
  };
};

// Hook for checking menu access
export const useMenuAccess = (menuPath: string) => {
  const { hasMenuAccess, loading } = useFeatures();
  
  return {
    hasAccess: hasMenuAccess(menuPath),
    loading
  };
};

// Hook for getting features by category
export const useFeaturesByCategory = (category: Feature['category']) => {
  const { getFeaturesByCategory, loading } = useFeatures();
  
  return {
    features: getFeaturesByCategory(category),
    loading
  };
};
